import { lazy, Suspense } from "react";
import { lensSet, useLens } from "../../lib/apiLensStore";
import { useT } from "../../lib/i18n";

/**
 * 接口视角（设计稿 apilens.jsx）的轻的那一半：顶栏开关、字段标记的壳、面板的壳。
 *
 * 登记表（约 400 项）、面板、选取层都在 ApiLensImpl.tsx，**开关打开后才加载**：Field / DataTable
 * 每页都会引这里，若直接引登记表，它就进了每个运营都要下的主包。开关关着时这里什么都不渲染。
 */

export const LENS_PANEL_W = 420;
const MONO = "font-mono text-[11px]";

const TagImpl = lazy(() => import("./ApiLensImpl"));
const PanelImpl = lazy(() => import("./ApiLensImpl").then((m) => ({ default: m.PanelImpl })));

/** 顶栏开关：打开时顺带展开面板，列出本页全部配置项 */
export function ApiLensToggle() {
  const t = useT();
  const s = useLens();
  return (
    <button type="button" data-lens-ui title={t("lens.toggleTip")} aria-pressed={s.on}
      onClick={() => lensSet({ on: !s.on, panel: !s.on, pick: false, sel: null, row: null })}
      className={`flex h-control-sm flex-none items-center gap-1.5 rounded-base border px-2.5 text-sm ${s.on
        ? "border-primary-300 bg-primary-50 text-primary" : "border-line bg-white text-ink-regular hover:border-primary hover:text-primary"}`}>
      <span className={MONO}>{"{ }"}</span>{t("lens.toggle")}
    </button>
  );
}

/** 接口视角开着没有。给只在开着时才改样式的地方用（Field / TField 的标签列） */
export function useLensOn(): boolean {
  return useLens().on;
}

/** 字段旁的字段名标记。开关关着时什么都不渲染，页面与原来一样 */
export function ApiTag({ id }: { id: string }) {
  const { on } = useLens();
  if (!on) return null;
  return <Suspense fallback={null}><TagImpl id={id} /></Suspense>;
}

/**
 * 右侧面板与选取层。停靠在内容区旁边（AppShell 按面板宽度给内容区让位），没有遮罩。
 * version：配置台当前打开的版本号；activeVersion：生效版本号；revision：这一版的修订号，变了（保存了）面板就重取一次值。
 */
export function ApiLensPanel(props: { version: number | null; activeVersion: number | null; revision: number | null }) {
  const { on } = useLens();
  if (!on) return null;
  return <Suspense fallback={null}><PanelImpl {...props} /></Suspense>;
}
